import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LifeBuoy, Menu, X,
  ChevronDown, Mail, Crown, Check,
  Wand2, Video, ShoppingBag, Swords, GraduationCap, Mic, LayoutGrid, Trophy, Briefcase,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import WhatsAppButton from "@/components/WhatsAppButton";
import HelpSupportModal from "@/components/HelpSupportModal";
import { ForRecruitersShowcase } from "@/components/landing/ForRecruitersShowcase";
import { HeroSection } from "@/components/landing/homepage/HeroSection";
import { ChooseYourGoalSection } from "@/components/landing/homepage/ChooseYourGoalSection";
import { HowItWorksSection } from "@/components/landing/homepage/HowItWorksSection";
import { AIToolsSection } from "@/components/landing/homepage/AIToolsSection";
import { SocialProofSection } from "@/components/landing/homepage/SocialProofSection";
import { CommunitySection } from "@/components/landing/homepage/CommunitySection";
import { FinalCTASection } from "@/components/landing/homepage/FinalCTASection";

const EXPLORE_LINKS = [
  { to: "/resume-builder", label: "AI Resume Builder", icon: Wand2 },
  { to: "/meetings", label: "Cloudivo Meet", icon: Video },
  { to: "/courses", label: "Courses", icon: ShoppingBag },
  { to: "/duel", label: "Duel", icon: Swords },
  { to: "/career", label: "Career", icon: GraduationCap },
  { to: "/interview", label: "Interview", icon: Mic },
  { to: "/resume-templates", label: "Resume Templates", icon: LayoutGrid },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
];

const FAQS = [
  { q: "What exactly is Cloudivo?", a: "Cloudivo is an all-in-one AI career platform built around three pillars: Learn (free 30-day daily challenges across Salesforce Data Cloud, Marketing Cloud/SFMC, MC Next and more, plus certification prep), Build (interactive simulators, playgrounds, real company case studies, an AI Resume Builder, and Cloudivo Meet for live sessions), and Get Hired (AI mock interviews with a talking avatar, a job board, recruiter-visible portfolio, and a real hiring pipeline). It also runs a Creator Studio, so you can learn from — or sell — real video courses." },
  { q: "Is the 30-Day Challenge really free?", a: "Yes — on all five tracks, every day, quiz, the Simulator/Playgrounds, Duel Mode, and Certification Prep are free. Premium ($9.99 one-time) adds unlimited AI mock interviews and a Premium badge on your public portfolio." },
  { q: "What makes the AI Interview Prep different from just practice questions?", a: "You get a real talking AI avatar interviewer on video, live voice rounds, a real SQL execution sandbox, and exam-grade proctoring (webcam + screen monitoring) — across Technical, SQL, Architecture, Behavioral and Scenario rounds, scored with a detailed AI report card." },
  { q: "Can I host meetings or classes on Cloudivo?", a: "Yes — Cloudivo Meet is a full LiveKit Cloud-powered video app with cloud recording + AI notes, breakout rooms, virtual backgrounds, and a Zoom-Webinar-style registration flow so people without an account can request to join a session you host." },
  { q: "Can I buy or sell video courses on Cloudivo?", a: "Yes — the Creator Studio lets anyone become a creator with a public profile and publish free or paid video courses (with AI-generated notes per lecture). Students can browse the marketplace, purchase via Stripe, and stream with a built-in notes panel." },
  { q: "Does Cloudivo have a job board like Naukri?", a: "Yes — Cloudivo runs its own job board with search, saved jobs, email alerts and AI-computed match scores for job seekers, plus a full recruiter side: a Kanban-style hiring pipeline and a verified Talent Directory scored on real, AI-verified skills instead of just a resume PDF." },
  { q: "Do I get a certificate?", a: "Yes — a verifiable, shareable certificate is generated automatically for each track you complete, with a LinkedIn-ready image." },
  { q: "Can recruiters find me on this platform?", a: "If you opt in, your public portfolio (projects, certificate, XP, badges) becomes visible to verified recruiters browsing the built-in Talent Directory." },
];

export default function LandingPage() {
  const [helpOpen, setHelpOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToHowItWorks = () => {
    document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-white overflow-x-hidden" data-testid="landing-page">
      {/* Public Nav */}
      <nav className="fixed top-0 w-full z-50 bg-[#0A101D]/70 backdrop-blur-xl border-b border-white/10" data-testid="public-navbar">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="landing-logo">
            <img src="/icons/cloudivo-mark.png" alt="Cloudivo" className="w-8 h-8 object-contain" />
            <span className="font-heading font-semibold">Cloudivo</span>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => setHelpOpen(true)}
              data-testid="nav-talk-to-advisor-btn"
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-[#5C9DFF] px-3 py-2 border border-white/10 rounded-lg transition-colors"
            >
              <LifeBuoy className="w-3.5 h-3.5" /> Talk to an Advisor
            </button>
            <Link to="/marketplace" data-testid="nav-marketplace-link" className="text-xs font-semibold text-slate-400 hover:text-[#5C9DFF] px-3 py-2 transition-colors">Explore All Skills</Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-explore-more-dropdown" className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-[#5C9DFF] px-3 py-2 transition-colors focus:outline-none">
                  Explore <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {EXPLORE_LINKS.map((link) => {
                  const Icon = link.icon;
                  return (
                    <DropdownMenuItem key={link.to} asChild data-testid={`nav-explore-item-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <Link to={link.to}><Icon className="w-4 h-4 mr-2" /> {link.label}</Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/recruiter" data-testid="nav-recruiter-link" className="text-xs font-semibold text-slate-400 hover:text-[#5C9DFF] px-3 py-2 border border-white/10 rounded-lg transition-colors">Hire Verified Talent</Link>
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-slate-300 hover:text-white px-4 py-2 transition-colors">Log In</Link>
            <Link to="/register" data-testid="nav-get-started-btn" className="text-sm font-semibold bg-primary hover:bg-primary/90 px-4 py-2 rounded-lg transition-colors">Get Started</Link>
          </div>
          <button
            onClick={() => setMobileMenuOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 text-white"
          >
            {mobileMenuOpen ? <X className="w-4.5 h-4.5" /> : <Menu className="w-4.5 h-4.5" />}
          </button>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
              className="md:hidden bg-slate-950 border-t border-white/10 overflow-hidden" data-testid="mobile-nav-menu"
            >
              <div className="px-6 py-5 flex flex-col gap-3">
                <button onClick={() => { setHelpOpen(true); setMobileMenuOpen(false); }} data-testid="mobile-nav-talk-to-advisor-btn" className="flex items-center gap-2 text-sm font-semibold text-slate-300 py-2.5">
                  <LifeBuoy className="w-4 h-4" /> Talk to an Advisor
                </button>
                <Link to="/marketplace" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-marketplace-link" className="text-sm font-semibold text-slate-300 py-2.5">Explore All Skills</Link>
                <div className="border-t border-white/10 my-1 pt-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">More to Explore</span>
                </div>
                {EXPLORE_LINKS.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link key={link.to} to={link.to} onClick={() => setMobileMenuOpen(false)} data-testid={`mobile-nav-explore-item-${link.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-2 text-sm font-semibold text-slate-300 py-2">
                      <Icon className="w-4 h-4 text-[#5C9DFF]" /> {link.label}
                    </Link>
                  );
                })}
                <Link to="/recruiter" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-recruiter-link" className="text-sm font-semibold text-slate-300 py-2.5 border-t border-white/10 mt-1 pt-3">Hire Verified Talent</Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-login-link" className="text-sm font-semibold text-slate-300 py-2.5">Log In</Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-get-started-btn" className="text-sm font-semibold text-center bg-primary hover:bg-primary/90 px-4 py-3 rounded-lg transition-colors">Get Started</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <HeroSection onWatchDemo={scrollToHowItWorks} />

      <ChooseYourGoalSection />

      <HowItWorksSection />

      <AIToolsSection />

      <SocialProofSection />

      <ForRecruitersShowcase />

      <CommunitySection />

      {/* Pricing */}
      <section id="pricing" className="max-w-5xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">Pricing</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Start Free. Upgrade When Ready.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
          <div className="rounded-2xl border border-white/10 bg-[#0A101D]/60 p-7" data-testid="pricing-free-card">
            <h3 className="font-heading text-lg font-semibold text-white mb-1">Free</h3>
            <p className="text-3xl font-bold text-white mb-4">$0</p>
            <ul className="space-y-2.5 mb-6">
              {["All 30 daily challenges, on any track", "Simulators, Playgrounds & Duel Mode", "Certification Prep (5 tracks)", "3 free AI mock interviews", "Browse the Course Marketplace"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {f}</li>
              ))}
            </ul>
            <Link to="/register" data-testid="pricing-free-cta" className="block text-center bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-6 py-3 rounded-xl transition-colors">Get Started Free</Link>
          </div>
          <div className="rounded-2xl border border-amber-400/30 bg-[#0A101D]/60 p-7 relative overflow-hidden" data-testid="pricing-premium-card">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 to-transparent" />
            <div className="relative">
              <h3 className="font-heading text-lg font-semibold text-white mb-1 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-400" /> Premium</h3>
              <p className="text-3xl font-bold text-white mb-1">$9.99<span className="text-sm text-slate-400 font-normal"> one-time</span></p>
              <ul className="space-y-2.5 mb-6 mt-4">
                {["Everything in Free", "Unlimited AI mock interviews", "Premium badge on your portfolio", "Priority visibility to recruiters"].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <Link to="/register" data-testid="pricing-premium-cta" className="block text-center bg-amber-400 hover:bg-amber-300 text-background font-bold px-6 py-3 rounded-xl transition-colors">Start Free, Upgrade Anytime</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-24">
        <div className="mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">FAQ</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={f.q} className="border border-white/10 rounded-xl overflow-hidden" data-testid={`faq-item-${i}`}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                data-testid={`faq-question-${i}`}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left bg-white/[0.02] hover:bg-white/[0.04] transition-colors"
              >
                <span className="text-sm font-medium text-white">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="px-5 py-4 text-sm text-slate-400 leading-relaxed border-t border-white/10" data-testid={`faq-answer-${i}`}>
                  {f.a}
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C9DFF]">Contact</span>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 mb-4">Still Have Questions?</h2>
        <p className="text-slate-400 mb-8">Talk to an advisor on WhatsApp, or drop us a line by email.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button onClick={() => setHelpOpen(true)} data-testid="contact-talk-to-advisor-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
            <LifeBuoy className="w-4 h-4" /> Talk to an Advisor
          </button>
          <a href="mailto:support@cloudivo.ai" data-testid="contact-email-link" className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-6 py-3 rounded-xl transition-colors">
            <Mail className="w-4 h-4" /> support@cloudivo.ai
          </a>
        </div>
      </section>

      <FinalCTASection />

      <footer className="border-t border-white/10 py-8 px-6 text-center" data-testid="landing-footer">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src="/icons/cloudivo-mark.png" alt="Cloudivo" className="w-5 h-5 object-contain" />
          <span className="font-heading font-semibold text-sm text-white">Cloudivo</span>
        </div>
        <p className="text-xs text-slate-400">© 2026 Cloudivo · cloudivo.ai — Learn. Build. Get Hired. All in one place, built for careers worldwide.</p>
      </footer>
      <WhatsAppButton />
      <HelpSupportModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
